import { AlertCircle, Bell01, CheckCircle, InfoCircle, XCircle, XClose, } from "../icons/v2-stroke";
import { assertNever } from "../lib/internal_utils";
import { cn } from "../lib/utils";
import React from "react";
import { Toaster, toast } from "sonner";
import { Button } from "./Button";
import { Icon } from "./Icon";
const NOTIFICATION_DELAY_MS = 5000;
const NotificationsContext = React.createContext((n) => n);
function resolveIcon(type) {
    switch (type) {
        case "success":
            return CheckCircle;
        case "error":
            return XCircle;
        case "info":
            return InfoCircle;
        case "warning":
            return AlertCircle;
        case "hello":
            return Bell01;
        default:
            return assertNever(type);
    }
}
function resolveIconColor(type) {
    switch (type) {
        case "success":
            return "text-success-500";
        case "error":
            return "text-warning-500";
        case "info":
            return "text-info-700";
        case "warning":
            return "text-amber-500";
        case "hello":
            return "text-primary-500";
        default:
            return assertNever(type);
    }
}
/**
 * The presentational card of a toast notification: icon, title, description,
 * optional action button, and optional dismiss control. Use it directly for
 * inline previews; in product code dispatch toasts with useSendNotification
 * instead.
 * @summary Presentational notification card.
 */
export function NotificationContent({ type, title, description, action, onDismiss, }) {
    const icon = resolveIcon(type);
    const iconColor = resolveIconColor(type);
    return (React.createElement("div", { className: cn("pointer-events-auto relative flex w-[246px] flex-col overflow-clip", "rounded-xl border border-border bg-background p-2", "shadow-[0px_0.5px_1px_0px_rgba(0,0,0,0.04),0px_1px_1px_0px_rgba(0,0,0,0.06),inset_2px_-2px_7px_0px_rgba(0,0,0,0.01),inset_0px_4px_4px_0px_rgba(255,255,255,0.08)]", "dark:shadow-[0px_2px_8px_0px_rgba(0,0,0,0.45),inset_0px_-1px_0px_0px_rgba(255,255,255,0.08)]", "dark:border-stone-800/60", "animate-in fade-in-0 zoom-in-95 duration-200 ease-emphasized", "origin-bottom-right motion-reduce:animate-none") },
        React.createElement("div", { className: "flex items-start justify-between gap-1" },
            React.createElement("div", { className: "flex min-w-0 flex-1 items-start gap-1" },
                React.createElement("div", { className: "mt-[2px] shrink-0" },
                    React.createElement(Icon, { visual: icon, size: "xs", className: iconColor, "aria-hidden": "true" })),
                React.createElement("div", { className: "flex min-w-0 flex-col" },
                    title && (React.createElement("span", { className: "text-sm font-medium leading-5 tracking-[-0.02em] text-foreground" }, title)),
                    description && (React.createElement("span", { className: "text-xs leading-4 text-muted-foreground" }, description)))),
            onDismiss && (React.createElement("button", { type: "button", onClick: onDismiss, className: "mt-[2px] shrink-0 cursor-pointer text-muted-foreground transition-colors hover:text-foreground", "aria-label": "Dismiss notification" },
                React.createElement(Icon, { visual: XClose, size: "xs" })))),
        action && (React.createElement("div", { className: "mt-1 pl-5" },
            React.createElement(Button, { size: "xs", variant: "ghost", label: action.label, onClick: () => {
                    action.onClick();
                    onDismiss === null || onDismiss === void 0 ? void 0 : onDismiss();
                } })))));
}
/**
 * A transient toast system that confirms the outcome of an action. Mount a
 * single Notification.Area near the app root, then dispatch toasts
 * imperatively with the useSendNotification hook. Use it for brief,
 * self-dismissing feedback after an action completes; for persistent, inline
 * status attached to a region, use ContentMessage instead.
 * @summary Toast notifications dispatched via hook.
 */
export const Notification = {
    Area: ({ children }) => {
        const sendNotification = React.useCallback((notification) => {
            toast.custom((t) => (React.createElement(NotificationContent, { type: notification.type, title: notification.title, description: notification.description, action: notification.action, onDismiss: () => toast.dismiss(t) })), {
                duration: NOTIFICATION_DELAY_MS,
            });
        }, []);
        return (React.createElement(NotificationsContext.Provider, { value: sendNotification },
            children,
            React.createElement(Toaster, { className: "flex flex-col items-end", duration: NOTIFICATION_DELAY_MS, visibleToasts: 5, closeButton: false, expand: false, invert: false, swipeDirections: ["right"], toastOptions: {
                    unstyled: true,
                    className: "w-fit select-none",
                } })));
    },
};
/**
 * Returns the function that dispatches a toast notification, provided by the
 * nearest Notification.Area.
 * @summary Hook to dispatch toast notifications.
 */
export const useSendNotification = () => React.useContext(NotificationsContext);
//# sourceMappingURL=Notification.js.map